import { TaxpeerLogo } from "@/components/taxpeerlogo.component";
import AuthService from "@/lib/auth/AuthApi.service";
import { useAuth } from "@/lib/contexts/auth.context";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { EmailStep } from "../components/loginEmail.component";
import { PasswordStep } from "../components/loginPassword.component";

/**
 * This is the login page of the application.
 * It is used to login the user.
 * There are two steps to login:
 * 1. Enter the email
 * 2. Enter the password
 * which are handled by the EmailStep and PasswordStep components.
 */
const LoginPage = () => {
	const [searchParams] = useSearchParams();
	const [step, setStep] = useState<"email" | "password">("email");
	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});
	const [_, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const { setAuthToken } = useAuth();
	const authService = AuthService.getInstance();

	// Email prefilling, returnTo parameter handling, and custom token authentication
	useEffect(() => {
		// Check for customAuthenticationToken in localStorage
		const customToken = localStorage.getItem("customAuthenticationToken");

		if (customToken) {
			// Remove the token immediately to prevent reuse
			localStorage.removeItem("customAuthenticationToken");

			// Use it to login
			(async () => {
				setIsLoading(true);
				try {
					const response = await authService.loginWithCustomToken(customToken);

					// Set auth token so AuthProvider picks up the user
					setAuthToken(response.token);

					// Get stored redirect URL
					const redirectUrl = localStorage.getItem("auth_redirect");
					const goToShop = localStorage.getItem("go_to_shop");

					// Add a small delay to ensure auth state is updated before navigation
					setTimeout(() => {
						if (redirectUrl) {
							// Clear the stored redirect after using it
							localStorage.removeItem("auth_redirect");
							navigate(redirectUrl);
						} else if (goToShop) {
							localStorage.removeItem("go_to_shop");
							navigate("/shop");
						} else {
							// Default navigation to dashboard
							navigate("/dashboard");
						}
					}, 300);
				} catch (error) {
					if (error instanceof Error) {
						setError(error.message);
					} else {
						setError("Fehler beim Login mit Token");
					}
					setIsLoading(false);
				}
			})();
			return; // Exit early if we found a custom token
		}

		// Get email from URL parameter
		const emailFromURL = searchParams.get("email");

		// Get returnTo parameter for redirection after login
		const returnTo = searchParams.get("returnTo");

		// Store returnTo in localStorage if present
		if (returnTo) {
			localStorage.setItem("auth_redirect", returnTo);
		}

		// Handle email prefilling if provided in URL
		if (emailFromURL && isValidEmail(emailFromURL)) {
			setFormData((prev) => ({
				...prev,
				email: emailFromURL,
			}));

			// Automatically proceed to password step if email is provided
			setStep("password");
		}
	}, [searchParams, authService, navigate /* setAuthToken*/]);

	// Helper function to validate email format
	const isValidEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleEmailSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setStep("password");
	};

	/**
	 * Handles the submission of the password step.
	 * It will try to login the user and if successful, it will redirect the user to the returnTo URL or the dashboard,
	 * depending on whether a returnTo URL is present.
	 * @param e - The form event.
	 */
	const handlePasswordSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);

		try {
			const response = await authService.login(formData);

			// Set auth token so AuthProvider picks up the user
			setAuthToken(response.token);

			// Get stored redirect URL
			const redirectUrl = localStorage.getItem("auth_redirect");
			const goToShop = localStorage.getItem("go_to_shop");
			// Add a small delay to ensure auth state is updated before navigation
			setTimeout(() => {
				if (redirectUrl) {
					// Clear the stored redirect after using it
					localStorage.removeItem("auth_redirect");
					navigate(redirectUrl);
				} else if (goToShop) {
					localStorage.removeItem("go_to_shop");
					navigate("/shop");
				} else {
					// Default navigation to dashboard
					navigate("/dashboard");
				}
			}, 300);
		} catch (error: unknown) {
			if (error instanceof Error) {
				setError(error.message);
			} else {
				setError("Fehler beim Login");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			if (step === "email") {
				handleEmailSubmit(e);
			} else if (step === "password") {
				handlePasswordSubmit(e);
			}
		}
	};

	return (
		<div
			className="flex min-h-screen flex-col items-center justify-center bg-card p-4"
			onKeyDown={handleKeyDown}
		>
			<div className="w-full max-w-[400px] space-y-8">
				{/* Logo */}
				<div className="flex justify-center">
					<TaxpeerLogo />
				</div>

				{/* Login Steps */}
				{step === "email" ? (
					<EmailStep
						email={formData.email}
						onEmailChange={(email) =>
							setFormData((prev) => ({ ...prev, email }))
						}
						onSubmit={handleEmailSubmit}
					/>
				) : (
					<PasswordStep
						email={formData.email}
						password={formData.password}
						onPasswordChange={(password) =>
							setFormData((prev) => ({ ...prev, password }))
						}
						onSubmit={handlePasswordSubmit}
						onEditEmail={() => setStep("email")}
						isLoading={isLoading}
					/>
				)}

				{/* Footer */}
				<div className="flex justify-center mt-6 space-x-4 text-sm text-muted-foreground">
					<Link to="/agb">AGB</Link>
					<Link to="/datenschutz">Datenschutzerklärung</Link>
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
